import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { RULES } from "@/constants/rulesTestIds";
import { PANELS, RULE_TYPES, SEVERITIES, SEVERITY_BLURBS } from "@/constants/rulesVocab";

export default function RuleAuthor() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleParse = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    setParsing(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post(`/brands/${brandId}/rules/parse`, { text });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Parser unavailable");
    } finally {
      setParsing(false);
    }
  };

  const openStructured = () => {
    // Surface B: skip parse, go straight to the structured form pre-filled with a blank Brand Note
    setResult({
      proposals: [
        {
          rule_type: "brand_note",
          severity: "informational",
          scope: { campaigns: [], formats: [], locations: [], location_groups: [], user_roles: [], panels: [] },
          parameters: { text: "" },
          explanation: "",
          source_text: text || "(structured entry)",
          confidence: "high",
          notes: null,
        },
      ],
      refusal: null,
      suggest_brand_note: false,
    });
  };

  return (
    <div data-testid={RULES.authorRoot} className="max-w-5xl">
      <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mb-3">Author a rule</p>
      <h1 className="font-serif text-5xl tracking-tight">In your own words.</h1>
      <p className="text-base text-[#3D3A35] mt-3 max-w-2xl">
        Paste or type guidance exactly as it appears in your brand book. Artisan
        will propose a structured interpretation; you confirm, edit, or
        convert it to a Brand Note.
      </p>

      <form onSubmit={handleParse} className="mt-10">
        <textarea
          data-testid={RULES.surfaceAInput}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          placeholder="e.g. Every wrap must include a return address on the mailing panel. Body copy is never set in primary brand teal."
          className="w-full px-5 py-4 border border-[#1A1F2C]/15 rounded-lg bg-white font-serif text-lg leading-relaxed focus:outline-none focus:border-[#B5462F] resize-y"
        />
        <div className="flex items-center gap-4 mt-4">
          <button
            type="submit"
            data-testid={RULES.surfaceAParseButton}
            disabled={parsing || !text.trim()}
            className="px-6 py-2.5 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium hover:bg-[#2A2F3C] disabled:opacity-50"
          >
            {parsing ? (
              <span data-testid={RULES.parseSpinner}>Parsing&hellip;</span>
            ) : (
              "Parse and review"
            )}
          </button>
          <button
            type="button"
            data-testid={RULES.surfaceAOpenStructured}
            onClick={openStructured}
            className="text-sm text-[#6B6358] hover:text-[#1A1F2C]"
          >
            I&rsquo;d rather build it from scratch &rarr;
          </button>
        </div>
        {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}
      </form>

      {result?.refusal && (
        <div data-testid={RULES.refusalBanner} className="mt-8 border border-[#B5462F]/30 bg-[#FDF1EC] rounded-lg p-6">
          <div className="text-xs uppercase tracking-[0.18em] text-[#B5462F] mb-2">
            Not parsed as a rule
          </div>
          <p className="text-sm text-[#3D3A35]">{result.refusal}</p>
          {result.suggest_brand_note && (
            <p className="text-sm text-[#3D3A35] mt-3">
              Would you like to save this as a <strong>Brand Note</strong> instead?
              Use the &ldquo;build it from scratch&rdquo; link above and pick the Brand Note type.
            </p>
          )}
        </div>
      )}

      {result?.proposals?.length > 0 && (
        <div className="mt-10 space-y-6">
          <h2 className="font-serif text-2xl">
            {result.proposals.length === 1
              ? "Proposed structured rule"
              : `Proposed structured rules · ${result.proposals.length}`}
          </h2>
          {result.proposals.map((p, idx) => (
            <ProposalCard
              key={idx}
              proposal={p}
              index={idx}
              brandId={brandId}
              onSaved={(ruleId) => navigate(`/brands/${brandId}/rules/${ruleId}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProposalCard({ proposal, index, brandId, onSaved }) {
  const [draft, setDraft] = useState({
    rule_type: proposal.rule_type,
    severity: proposal.severity,
    explanation: proposal.explanation || "",
    parameters: proposal.parameters || {},
    scope: proposal.scope || {
      campaigns: [], formats: [], locations: [], location_groups: [], user_roles: [], panels: [],
    },
    source_text: proposal.source_text,
  });
  const [paramsText, setParamsText] = useState(JSON.stringify(proposal.parameters || {}, null, 2));
  const [saving, setSaving] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [error, setError] = useState("");

  if (rejected) {
    return (
      <div
        data-testid={`${RULES.proposalPrefix}${index}`}
        className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 opacity-60"
      >
        <p className="text-sm text-[#6B6358]">Proposal rejected.</p>
      </div>
    );
  }

  const toggleArrayValue = (dim, value) => {
    const current = draft.scope[dim] || [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setDraft({ ...draft, scope: { ...draft.scope, [dim]: next } });
  };

  const save = async () => {
    setError("");
    let params;
    try {
      params = JSON.parse(paramsText);
    } catch {
      setError("Parameters must be valid JSON");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/brands/${brandId}/rules`, {
        rule_type: draft.rule_type,
        severity: draft.severity,
        scope: draft.scope,
        parameters: params,
        explanation: draft.explanation,
        source_text: draft.source_text,
      });
      onSaved(data.rule_id);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save rule");
    } finally {
      setSaving(false);
    }
  };

  const isBrandNote = draft.rule_type === "brand_note";

  return (
    <div
      data-testid={`${RULES.proposalPrefix}${index}`}
      className="border border-[#1A1F2C]/10 bg-white rounded-lg p-8"
    >
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-[#6B6358]">
          <span>Confidence:</span>
          <span className={proposal.confidence === "high" ? "text-[#1A1F2C]" : "text-[#B5462F]"}>
            {proposal.confidence}
          </span>
          {proposal.notes && <span className="italic text-[#B5462F] normal-case tracking-normal">{proposal.notes}</span>}
        </div>
      </div>

      <Field label="Rule type">
        <select
          data-testid={`${RULES.proposalTypeSelect}${index}`}
          value={draft.rule_type}
          onChange={(e) => setDraft({ ...draft, rule_type: e.target.value })}
          className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
        >
          {RULE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {`${t.label} (Tier ${t.tier})`}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Severity" hint={SEVERITY_BLURBS[draft.severity]}>
        <select
          data-testid={`${RULES.proposalSeveritySelect}${index}`}
          value={draft.severity}
          onChange={(e) => setDraft({ ...draft, severity: e.target.value })}
          className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
        >
          {SEVERITIES.map((s) => (
            <option key={s.value} value={s.value}>{`${s.label}`}</option>
          ))}
        </select>
      </Field>

      <Field label="Explanation">
        <textarea
          data-testid={`${RULES.proposalExplanationInput}${index}`}
          rows={2}
          value={draft.explanation}
          onChange={(e) => setDraft({ ...draft, explanation: e.target.value })}
          className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
        />
      </Field>

      <Field label="Scope — formats">
        <div className="flex flex-wrap gap-2" data-testid={`${RULES.proposalScopeFormats}${index}`}>
          {["six_panel_wrap"].map((f) => (
            <Chip
              key={f}
              active={draft.scope.formats?.includes(f)}
              onClick={() => toggleArrayValue("formats", f)}
            >
              {f}
            </Chip>
          ))}
          <span className="text-xs text-[#6B6358] self-center ml-2">Empty = brand-wide</span>
        </div>
      </Field>

      <Field label="Scope — panels">
        <div className="flex flex-wrap gap-2" data-testid={`${RULES.proposalScopePanels}${index}`}>
          {PANELS.map((p) => (
            <Chip
              key={p}
              active={draft.scope.panels?.includes(p)}
              onClick={() => toggleArrayValue("panels", p)}
            >
              {p}
            </Chip>
          ))}
        </div>
      </Field>

      <Field
        label={`Parameters · ${draft.rule_type}`}
        hint={isBrandNote ? "Brand Notes carry advisory text; not enforced by the Compliance Engine." : null}
      >
        <textarea
          data-testid={`${RULES.proposalParamsTextarea}${index}`}
          rows={isBrandNote ? 3 : 6}
          value={paramsText}
          onChange={(e) => setParamsText(e.target.value)}
          className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] font-mono text-xs"
        />
      </Field>

      {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}

      <div className="flex items-center gap-3 mt-6 pt-6 border-t border-[#1A1F2C]/10">
        <button
          data-testid={`${RULES.proposalAcceptButton}${index}`}
          onClick={save}
          disabled={saving}
          className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium disabled:opacity-50"
        >
          {saving ? "Saving..." : "Accept as draft"}
        </button>
        <button
          data-testid={`${RULES.proposalSaveAsNote}${index}`}
          onClick={() => setDraft({ ...draft, rule_type: "brand_note", parameters: { text: draft.source_text } })}
          className="px-5 py-2 text-sm text-[#6B6358] hover:text-[#1A1F2C]"
        >
          Convert to Brand Note
        </button>
        <button
          data-testid={`${RULES.proposalRejectButton}${index}`}
          onClick={() => setRejected(true)}
          className="ml-auto px-5 py-2 text-sm text-[#6B6358] hover:text-[#B5462F]"
        >
          Reject this proposal
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-5">
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#6B6358] mb-1.5">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-[#6B6358] mt-1 italic">{hint}</p>}
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs ${
        active
          ? "bg-[#1A1F2C] text-[#F8F5EE]"
          : "border border-[#1A1F2C]/20 text-[#3D3A35] hover:border-[#1A1F2C]/40"
      }`}
    >
      {children}
    </button>
  );
}
